import {IFileDesc, IStuffToUpload, IStuffBeingUploaded, IFinishedUpload} from '@app-types/websocket/file.types';
import {commonWebSocket} from '@services/common/common.constants';
import {createReducer} from 'redux-act';
import {
  removeStoredFile,
  storeFileWhileAwaitingUploadslot,
  suppressLoadingScreenForFilePickerUntil,
  uploadedFileUsed,
  uploadFailed,
  uploadSlotRejected,
  uploadProgress,
  uploadSuccess,
} from './file.actions';
import { uploadFile } from './file.http';

interface IFileState {
  uploadSlots:IStuffBeingUploaded[],
  uploadedFiles:IFinishedUpload[],
  dataToUpload:IStuffToUpload[],
  filePickerLoadingScreenSuppressedUntil:number,
}

const defaultState: IFileState = {
  uploadSlots:[],
  uploadedFiles:[],
  dataToUpload:[],
  filePickerLoadingScreenSuppressedUntil:0,
};

declare let window: any;

const MAX_STORED_UPLOAD_SLOT_WAIT_MS = 30000;

function asArray<T>(items: any): T[] {
  return Array.isArray(items) ? items : [];
}

function isFreshStoredUpload(upload: IStuffToUpload, now: number = Date.now()): boolean {
  return !!upload
    && typeof upload.internalId === "number"
    && typeof upload.requestedAt === "number"
    && now - upload.requestedAt <= MAX_STORED_UPLOAD_SLOT_WAIT_MS;
}

function freshDataToUpload(dataToUpload: any): IStuffToUpload[] {
  const now = Date.now();
  return asArray<IStuffToUpload>(dataToUpload).filter((upload) => isFreshStoredUpload(upload, now));
}

const reducer = createReducer<typeof defaultState>({}, defaultState);

reducer
  .on(suppressLoadingScreenForFilePickerUntil, (state: any, payload: number) => {
    const suppressUntil = typeof payload === "number" && Number.isFinite(payload) ? payload : 0;
    // console.log("Suppressing loading screen for file picker.", { suppressUntil });
    return {
      ...state,
      uploadSlots: asArray<IStuffBeingUploaded>(state.uploadSlots),
      uploadedFiles: asArray<IFinishedUpload>(state.uploadedFiles),
      dataToUpload: freshDataToUpload(state.dataToUpload),
      filePickerLoadingScreenSuppressedUntil: Math.max(state.filePickerLoadingScreenSuppressedUntil || 0, suppressUntil),
    };
  })
  .on(storeFileWhileAwaitingUploadslot, (state: any, payload: IStuffToUpload) => {
    if (!payload || typeof payload.internalId !== "number") {
      return {
        ...state,
        uploadSlots: asArray<IStuffBeingUploaded>(state.uploadSlots),
        uploadedFiles: asArray<IFinishedUpload>(state.uploadedFiles),
        dataToUpload: freshDataToUpload(state.dataToUpload),
      };
    }

    const requestedAt = typeof payload.requestedAt === "number" ? payload.requestedAt : Date.now();
    const storedUpload = {
      ...payload,
      requestedAt,
    };
    const existingUploads = freshDataToUpload(state.dataToUpload)
      .filter((upload) => upload.internalId !== storedUpload.internalId);

    return {
      ...state,
      uploadSlots: asArray<IStuffBeingUploaded>(state.uploadSlots),
      uploadedFiles: asArray<IFinishedUpload>(state.uploadedFiles),
      dataToUpload: [...existingUploads, storedUpload],
    };
  })
  .on(removeStoredFile, (state: IFileState, uploadedFile: number) => {
    const uploadedFiles = asArray<IFinishedUpload>(state.uploadedFiles);
    const uploadSlots = asArray<IStuffBeingUploaded>(state.uploadSlots);

    return {
      ...state,
      uploadedFiles: uploadedFiles.filter((file: IFinishedUpload) => file.id !== uploadedFile),
      uploadSlots: uploadSlots.filter((f: IStuffBeingUploaded) => f.id !== uploadedFile),
      dataToUpload: freshDataToUpload(state.dataToUpload),
    };
  })
  .on(uploadProgress, (state: IFileState, progressFile: IStuffBeingUploaded) => {
    const uploadSlots:IStuffBeingUploaded[] = asArray<IStuffBeingUploaded>(state.uploadSlots).slice();
    if (!progressFile) {
      return {
        ...state,
        uploadedFiles: asArray<IFinishedUpload>(state.uploadedFiles),
        dataToUpload: freshDataToUpload(state.dataToUpload),
        uploadSlots
      };
    }

    const fileToUpdateIndex = uploadSlots.findIndex((file: IStuffBeingUploaded) => (file.id === progressFile.id));

    if (fileToUpdateIndex > -1)
    {
      uploadSlots[fileToUpdateIndex] = progressFile;
    } else {
      console.log("update on untracked file!");
    }

    return {
      ...state,
      uploadedFiles: asArray<IFinishedUpload>(state.uploadedFiles),
      dataToUpload: freshDataToUpload(state.dataToUpload),
      uploadSlots
    };
  })
  .on(uploadFailed, (state: IFileState,
    uploadedFile: IStuffBeingUploaded) => {
    const uploadedFiles = asArray<IFinishedUpload>(state.uploadedFiles);
    const uploadSlots = asArray<IStuffBeingUploaded>(state.uploadSlots);
    const dataToUpload = freshDataToUpload(state.dataToUpload);
    const uploadedFileId = uploadedFile ? uploadedFile.id : undefined;
    const uploadedFileInternalId = uploadedFile ? uploadedFile.internalId : undefined;

    return {
      ...state,
      uploadedFiles,
      uploadSlots: uploadSlots.filter((f: IStuffBeingUploaded) => f.id !== uploadedFileId),
      dataToUpload: dataToUpload.filter((f: IStuffToUpload) => f.internalId !== uploadedFileInternalId),
    };
  })
  .on(uploadSlotRejected, (state: IFileState, rejection: {internalId:number}) => {
    const internalId = rejection ? rejection.internalId : undefined;
    return {
      ...state,
      uploadedFiles: asArray<IFinishedUpload>(state.uploadedFiles),
      uploadSlots: asArray<IStuffBeingUploaded>(state.uploadSlots)
        .filter((file) => file.internalId !== internalId),
      dataToUpload: freshDataToUpload(state.dataToUpload)
        .filter((file) => file.internalId !== internalId),
    };
  })
  .on(uploadSuccess, (state: IFileState,
    uploadedFile: IStuffBeingUploaded) => {
    const uploadedFiles = asArray<IFinishedUpload>(state.uploadedFiles);
    const uploadSlots = asArray<IStuffBeingUploaded>(state.uploadSlots);
    const dataToUpload = freshDataToUpload(state.dataToUpload);
    if (!uploadedFile) {
      return {
        ...state,
        uploadedFiles,
        uploadSlots,
        dataToUpload,
      };
    }

    const finishedUpload:IFinishedUpload = {
      name: uploadedFile.name,
      id: uploadedFile.id,
      fileName: uploadedFile.fileName,
      fileSize: uploadedFile.fileSize,
      file: undefined,
      data: undefined,
      internalId: uploadedFile.internalId,
      localURL:uploadedFile.localURL,
      permanentURL:uploadedFile.permanentURL,
    };

    return {
      ...state,
      uploadedFiles:[...uploadedFiles, finishedUpload],
      uploadSlots: uploadSlots.filter((f: IStuffBeingUploaded) => f.id !== uploadedFile.id),
      dataToUpload: dataToUpload.filter((f:IStuffToUpload) => f.internalId !== uploadedFile.internalId),
    };
  })
  .on(uploadedFileUsed, (state: IFileState,
    usedFile: number) => {
    const uploadedFiles = asArray<IFinishedUpload>(state.uploadedFiles);
    const uploadSlots = asArray<IStuffBeingUploaded>(state.uploadSlots);

    // TODO: delete files that no longer are needed from server!!
    return {
      ...state,
      // dataToUpload: state.dataToUpload.filter((f:IStuffToUpload) => f.internalId !== usedFile.internalId),
      uploadedFiles: uploadedFiles.filter((f: IFinishedUpload) => f.id !== usedFile),
      uploadSlots: uploadSlots.filter((f: IStuffBeingUploaded) => f.id !== usedFile),
      dataToUpload: freshDataToUpload(state.dataToUpload),
    };
  })
  // we are given a PUT file slot from backend. lets start uploading data that is waiting!
  .on(commonWebSocket.special.FILE_UPLOAD, (
    state:IFileState,
    payload:{uploadFile:IFileDesc}) => {

    const uploadSlots = asArray<IStuffBeingUploaded>(state.uploadSlots);
    const uploadedFiles = asArray<IFinishedUpload>(state.uploadedFiles);
    const activeDataToUpload = freshDataToUpload(state.dataToUpload);
    if (!payload || !payload.uploadFile) {
      return {
        ...state,
        uploadSlots,
        uploadedFiles,
        dataToUpload: activeDataToUpload,
      };
    }

    const internalId = payload.uploadFile.internalId?parseInt(payload.uploadFile.internalId, 10):0;
    if (!internalId) {
      return {
        ...state,
        uploadSlots,
        uploadedFiles,
        dataToUpload: activeDataToUpload,
      };
    }

    const dataToUpload = activeDataToUpload.find((data) => data.internalId === internalId);
    const alreadyHasSlot = uploadSlots.some((slot) => slot.internalId === internalId);
    const alreadyUploaded = uploadedFiles.some((file) => file.internalId === internalId);

    if (!dataToUpload || alreadyHasSlot || alreadyUploaded) {
      return {
        ...state,
        uploadSlots,
        uploadedFiles,
        dataToUpload: activeDataToUpload,
      };
    }

    const newSlot:IStuffBeingUploaded = {
      name: payload.uploadFile.fileName,
      id: payload.uploadFile.id!,
      internalId,

      fileName: payload.uploadFile.fileName,
      fileSize: payload.uploadFile.fileSize,
      loaded: 0,
      url:payload.uploadFile.url!, // upload url
      permanentURL:payload.uploadFile.permanentURL,
    
      file: undefined,
      data: undefined,
    };

    const newDataToUpload:IStuffToUpload[] = []
    for (const storedUpload of activeDataToUpload) {
      if (storedUpload.internalId === newSlot.internalId) { // this is a matching slot!
        newSlot.localURL = storedUpload.localURL;
      } else {
        newDataToUpload.push(storedUpload)
      }
    }
    return {
      ...state,
      uploadedFiles,
      uploadSlots: [...uploadSlots, newSlot],
      dataToUpload:newDataToUpload,
    };
    // this will trigger in public router to do the actual upload
  });

export default reducer;



// WEBPACK FOOTER //
// ./src/services/file/file.reducer.ts